import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Trash2, Loader2, Users, Edit } from "lucide-react";
import { format } from "date-fns";
import type { Account, AccountPeople, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ContactFormModal } from "@/components/contact-form-modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StructureDatePicker } from "@/components/ui/structure-date-picker";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl } from "@/lib/breadcrumbs";

interface DirectorWithContact extends AccountPeople {
  contact?: Contact;
}

interface DirectorsPanelProps {
  account: Account;
}

const SHARE_CLASS_OPTIONS = [
  { value: "ordinary", label: "Ordinary" },
  { value: "class_a", label: "Class A" },
  { value: "class_b", label: "Class B" },
  { value: "class_c", label: "Class C" },
  { value: "class_d", label: "Class D" },
  { value: "class_e", label: "Class E" },
  { value: "preference", label: "Preference" },
  { value: "redeemable_preference", label: "Redeemable Preference" },
  { value: "performance", label: "Performance" },
  { value: "employee", label: "Employee" },
];

export function DirectorsPanel({ account }: DirectorsPanelProps) {
  const [, navigate] = useLocation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const [editingDirector, setEditingDirector] = useState<DirectorWithContact | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Date | undefined>();
  const [ceasedDate, setCeasedDate] = useState<Date | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Also add as shareholder/beneficial owner checkboxes (Add mode)
  const [alsoAddAsShareholder, setAlsoAddAsShareholder] = useState(false);
  const [alsoAddAsBeneficialOwner, setAlsoAddAsBeneficialOwner] = useState(false);

  // Shareholder fields (only shown when alsoAddAsShareholder is true)
  const [shareClass, setShareClass] = useState<string>("ordinary");
  const [numberOfShares, setNumberOfShares] = useState<string>("");
  const [ownershipPercentage, setOwnershipPercentage] = useState<string>("");

  // EDG-817 N-5: Local submitting state for Save button (avoids race with onSuccess clearing isPending)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // EDG-726 F002: Edit mode composition state (pre-ticked based on existing roles)
  const [editIsShareholder, setEditIsShareholder] = useState(false);
  const [editIsBeneficialOwner, setEditIsBeneficialOwner] = useState(false);
  const [editOriginalIsShareholder, setEditOriginalIsShareholder] = useState(false);
  const [editOriginalIsBeneficialOwner, setEditOriginalIsBeneficialOwner] = useState(false);

  const { toast } = useToast();

  const { data: directors = [], isLoading } = useQuery<DirectorWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "directors"],
    enabled: account.entityType === "company",
  });

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }],
    enabled: isAddDialogOpen,
  });

  // ISSUE-162: Get existing director contact IDs to prevent duplicates
  const existingDirectorContactIds = useMemo(() => {
    return directors.map((director) => director.contactId);
  }, [directors]);

  const addMutation = useMutation({
    mutationFn: async (data: {
      contactId: string;
      appointmentDate?: string;
      alsoAddAsShareholder?: boolean;
      alsoAddAsBeneficialOwner?: boolean;
      shareClass?: string;
      numberOfShares?: string;
      ownershipPercentage?: string;
    }) => {
      // EDG-726 F002: Use atomic composite endpoint for transactional multi-role creation
      const compositions: Array<Record<string, unknown>> = [];

      if (data.alsoAddAsShareholder) {
        compositions.push({
          type: "shareholder",
          shareClass: data.shareClass || "ordinary",
          numberOfShares: data.numberOfShares || undefined,
          ownershipPercentage: data.ownershipPercentage || undefined,
        });
      }

      if (data.alsoAddAsBeneficialOwner) {
        compositions.push({ type: "beneficial_owner" });
      }

      await apiRequest("POST", "/api/crm/structure/composite", {
        accountId: account.id,
        contactId: data.contactId,
        primaryRole: {
          type: "director",
          appointmentDate: data.appointmentDate,
        },
        compositions,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "directors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      if (variables.alsoAddAsBeneficialOwner) {
        queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
      }
      if (variables.alsoAddAsShareholder) {
        queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
      }

      let description = "The director has been added successfully.";
      if (variables.alsoAddAsShareholder && variables.alsoAddAsBeneficialOwner) {
        description = "The director has been added and recorded as shareholder and beneficial owner.";
      } else if (variables.alsoAddAsShareholder) {
        description = "The director has been added and recorded as shareholder.";
      } else if (variables.alsoAddAsBeneficialOwner) {
        description = "The director has been added and recorded as beneficial owner.";
      }

      toast({ title: "Director added", description });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add director", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "directors"] });
      toast({ title: "Director removed", description: "The director has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove director", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      appointmentDate?: string;
      ceasedDate?: string;
      notes?: string;
    }) => {
      const { id, ...updateData } = data;
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "directors"] });
      toast({ title: "Director updated", description: "The director has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update director", variant: "destructive" });
    },
  });

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedContactId(null);
    setAppointmentDate(undefined);
    setCeasedDate(undefined);
    setAlsoAddAsShareholder(false);
    setAlsoAddAsBeneficialOwner(false);
    setShareClass("ordinary");
    setNumberOfShares("");
    setOwnershipPercentage("");
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingDirector(null);
    setAppointmentDate(undefined);
    setCeasedDate(undefined);
    setEditIsShareholder(false);
    setEditIsBeneficialOwner(false);
    setEditOriginalIsShareholder(false);
    setEditOriginalIsBeneficialOwner(false);
  };

  const handleOpenEdit = async (director: DirectorWithContact) => {
    setEditingDirector(director);
    setAppointmentDate(director.appointmentDate ? new Date(director.appointmentDate) : undefined);
    setCeasedDate(director.ceasedDate ? new Date(director.ceasedDate) : undefined);

    // EDG-726 F002: Check existing roles for this contact on this account
    try {
      const peopleRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/people`);
      const people = await peopleRes.json();
      const contactRoles = people.filter(
        (p: any) => p.contactId === director.contactId && !p.ceasedDate,
      );
      const shareholderRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/shareholders`);
      const shareholderList = await shareholderRes.json();
      const isShareholderMatch = shareholderList.some(
        (s: any) => s.contactId === director.contactId && !s.ceasedDate,
      );
      const isBOMatch = contactRoles.some((p: any) => p.role === "beneficial_owner");

      setEditIsShareholder(isShareholderMatch);
      setEditIsBeneficialOwner(isBOMatch);
      setEditOriginalIsShareholder(isShareholderMatch);
      setEditOriginalIsBeneficialOwner(isBOMatch);
      // EDG-817 N-6: Only open the dialog after role data is successfully fetched
      setIsEditDialogOpen(true);
    } catch {
      setEditIsShareholder(false);
      setEditIsBeneficialOwner(false);
      setEditOriginalIsShareholder(false);
      setEditOriginalIsBeneficialOwner(false);
      toast({ title: "Error", description: "Failed to load role data for editing", variant: "destructive" });
    }
  };

  const handleUpdate = async () => {
    if (!editingDirector) return;

    setIsSubmitting(true);
    // EDG-817 P1-C: Use mutateAsync so composition calls only run if the primary update succeeds
    try {
      await updateMutation.mutateAsync({
        id: editingDirector.id,
        appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
        ceasedDate: ceasedDate ? format(ceasedDate, "yyyy-MM-dd") : undefined,
        notes: editingDirector.notes || undefined,
      });
    } catch {
      // onError handler in useMutation already shows a toast — abort composition changes
      setIsSubmitting(false);
      return;
    }

    // EDG-726 F002: Handle composition changes (newly ticked or un-ticked)
    const today = format(new Date(), "yyyy-MM-dd");

    // Add beneficial_owner if newly ticked
    if (editIsBeneficialOwner && !editOriginalIsBeneficialOwner) {
      await apiRequest("POST", "/api/crm/structure/composite", {
        accountId: account.id,
        contactId: editingDirector.contactId,
        primaryRole: { type: "director" }, // idempotent — already exists
        compositions: [{ type: "beneficial_owner" }],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
    }

    // Cease beneficial_owner if un-ticked
    if (!editIsBeneficialOwner && editOriginalIsBeneficialOwner) {
      const boRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/people?role=beneficial_owner`);
      const boList = await boRes.json();
      const boRow = boList.find(
        (p: any) => p.contactId === editingDirector.contactId && !p.ceasedDate,
      );
      if (boRow) {
        await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${boRow.id}`, {
          ceasedDate: today,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners", "derived"] });
    }

    // Add shareholder if newly ticked
    if (editIsShareholder && !editOriginalIsShareholder) {
      await apiRequest("POST", "/api/crm/structure/composite", {
        accountId: account.id,
        contactId: editingDirector.contactId,
        primaryRole: { type: "director" },
        compositions: [{ type: "shareholder" }],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
    }

    // Cease shareholder if un-ticked — use shareholders endpoint directly
    if (!editIsShareholder && editOriginalIsShareholder) {
      const shRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/shareholders`);
      const shList = await shRes.json();
      const shRow = shList.find(
        (s: any) => s.contactId === editingDirector.contactId && !s.ceasedDate,
      );
      if (shRow) {
        await apiRequest("PATCH", `/api/crm/accounts/${account.id}/shareholders/${shRow.id}`, {
          ceasedDate: today,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
    }

    setIsSubmitting(false);
  };

  const handleAdd = () => {
    if (!selectedContactId) return;

    addMutation.mutate({
      contactId: selectedContactId,
      appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
      alsoAddAsShareholder,
      alsoAddAsBeneficialOwner,
      shareClass: alsoAddAsShareholder ? shareClass : undefined,
      numberOfShares: alsoAddAsShareholder && numberOfShares ? numberOfShares : undefined,
      ownershipPercentage:
        alsoAddAsShareholder && ownershipPercentage ? ownershipPercentage : undefined,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContactName = (director: DirectorWithContact) => {
    if (director.contact) {
      return `${director.contact.firstName || ""} ${director.contact.lastName || ""}`.trim() || "Unknown";
    }
    const contact = contacts.find(c => c.id === director.contactId);
    if (contact) {
      return `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "Unknown";
    }
    return "Unknown";
  };

  if (account.entityType !== "company") {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="directors-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">DIRECTORS</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-director"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Director
        </Button>
      </div>

      {directors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <Users className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No directors yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Director" to add a director to this company
          </p>
        </div>
      ) : (
        <div className="border rounded-md bg-white">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Contact Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Appointment Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Ceased Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Notes</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {directors.map((director) => (
                <TableRow
                  key={director.id}
                  className="h-10"
                  data-testid={`row-director-${director.id}`}
                >
                  <TableCell className="px-2 py-1">
                    <button
                      onClick={() => {
                        const url = buildNavigationUrl(`/contacts/${director.contactId}`, {
                          from: 'account',
                          fromId: account.id,
                          fromLabel: account.legalName || 'Account',
                        });
                        navigate(url);
                      }}
                      className="text-primary hover:underline text-sm"
                      data-testid={`link-contact-${director.id}`}
                    >
                      {getContactName(director)}
                    </button>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(director.appointmentDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(director.ceasedDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm text-muted-foreground truncate max-w-[200px]">
                    {director.notes || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(director)}
                        data-testid={`button-edit-director-${director.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(director.id)}
                      data-testid={`button-delete-director-${director.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Director</DialogTitle>
            <DialogDescription>
              Select a contact from the client group to add as a director.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact</Label>
              <ContactSelect
                value={selectedContactId}
                onValueChange={setSelectedContactId}
                clientGroupId={account.clientGroupId}
                excludeContactIds={existingDirectorContactIds}
                placeholder="Select a contact"
                data-testid="select-director-contact"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={appointmentDate}
                onChange={setAppointmentDate}
                testId="input-director-appointment-date"
              />
            </div>

            {/* Also add as Shareholder checkbox */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="also-add-shareholder"
                checked={alsoAddAsShareholder}
                onCheckedChange={(checked) => setAlsoAddAsShareholder(checked === true)}
                data-testid="checkbox-also-add-shareholder"
              />
              <Label htmlFor="also-add-shareholder" className="text-sm font-normal cursor-pointer">
                Also add as Shareholder
              </Label>
            </div>

            {/* Shareholder fields (conditional) */}
            {alsoAddAsShareholder && (
              <div className="space-y-3 pt-2 pl-6 border-l-2 border-muted">
                <div className="space-y-2">
                  <Label>Share Class</Label>
                  <Select value={shareClass} onValueChange={setShareClass}>
                    <SelectTrigger data-testid="select-share-class">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SHARE_CLASS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Shares (optional)</Label>
                  <Input
                    type="text"
                    value={numberOfShares}
                    onChange={(e) => setNumberOfShares(e.target.value)}
                    placeholder="e.g., 1000"
                    data-testid="input-number-of-shares"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ownership % (optional)</Label>
                  <Input
                    type="text"
                    value={ownershipPercentage}
                    onChange={(e) => setOwnershipPercentage(e.target.value)}
                    placeholder="e.g., 50.00"
                    data-testid="input-ownership-percentage"
                  />
                </div>
              </div>
            )}

            {/* Also add as Beneficial Owner checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="also-add-beneficial-owner"
                checked={alsoAddAsBeneficialOwner}
                onCheckedChange={(checked) => setAlsoAddAsBeneficialOwner(checked === true)}
                data-testid="checkbox-also-add-beneficial-owner"
              />
              <Label
                htmlFor="also-add-beneficial-owner"
                className="text-sm font-normal cursor-pointer"
              >
                Also add as Beneficial Owner
              </Label>
            </div>

            {/* Note: Ownership % is shared with shareholder if both are checked */}
            {/* ISSUE-160 FIX: Removed ownership % field for beneficial owner only
                Beneficial owners in crm_x_account_people don't have ownership_percentage
                Only shareholders (crm_x_account_shareholders table) have ownership_percentage */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!selectedContactId || addMutation.isPending}
              data-testid="button-confirm-add-director"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Director
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Director Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Director</DialogTitle>
            <DialogDescription>
              Update the appointment details for this director.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={editingDirector ? getContactName(editingDirector) : ""}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label>Appointment Date</Label>
              <StructureDatePicker
                value={appointmentDate}
                onChange={setAppointmentDate}
                testId="edit-input-director-appointment-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Ceased Date</Label>
              <StructureDatePicker
                value={ceasedDate}
                onChange={setCeasedDate}
                testId="edit-input-director-ceased-date"
              />
            </div>

            {/* EDG-726 F002: Composition checkboxes in Edit mode (parity with Add) */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="edit-also-shareholder"
                checked={editIsShareholder}
                onCheckedChange={(checked) => setEditIsShareholder(checked === true)}
                data-testid="edit-checkbox-also-shareholder"
              />
              <Label htmlFor="edit-also-shareholder" className="text-sm font-normal cursor-pointer">
                Also a Shareholder
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-also-beneficial-owner"
                checked={editIsBeneficialOwner}
                onCheckedChange={(checked) => setEditIsBeneficialOwner(checked === true)}
                data-testid="edit-checkbox-also-beneficial-owner"
              />
              <Label htmlFor="edit-also-beneficial-owner" className="text-sm font-normal cursor-pointer">
                Also a Beneficial Owner
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={isSubmitting}
              data-testid="button-confirm-update-director"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Director
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Director</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this director? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-director"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Creation Modal */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={(newContact: Contact) => {
          setSelectedContactId(newContact.id);
          setShowCreateContactDialog(false);
          queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
          toast({ title: "Contact created", description: "You can now select the newly created contact." });
        }}
        defaultClientGroupId={account.clientGroupId}
      />
    </div>
  );
}
